<template>
  <div class="pagination" v-if="isShowPagination">
    <ul class="pagination__list">
      <li
        class="pagination__item pagination__arrow"
        @click="itemClick(currentPage - 1)"
        :class="{ 'pagination__arrow--hidden': currentPage <= 1 }"
      >
        <img :src="DoubleLeftIcon" alt="left arrow" width="48" height="48" />
      </li>
      <li
        class="pagination__item"
        :class="itemClasses(page)"
        @click="itemClick(page)"
        v-for="page of pages"
        :key="page"
      >
        {{ page }}
      </li>
      <li
        class="pagination__item pagination__arrow"
        @click="itemClick(currentPage + 1)"
        :class="{
          'pagination__arrow--hidden': currentPage >= numberOfPage,
        }"
      >
        <img :src="DoubleRightIcon" alt="left arrow" width="48" height="48" />
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import DoubleLeftIcon from '@/assets/images/icons/double-left-arrows.png'
import DoubleRightIcon from '@/assets/images/icons/double-right-arrows.png'
import { computed, ComputedRef } from '@vue/reactivity'

interface PaginationProp {
  numberOfPage: number
  currentPage: number
}

type Page = number | string

const props = withDefaults(defineProps<PaginationProp>(), {
  numberOfPage: 1,
  currentPage: 1,
})

const emit = defineEmits<{
  (e: 'input', value: number): void
}>()

const thresholdToShowTheEllipsis = 2
const ellipsis = '...'

const isShowPagination = computed(() => {
  return props.numberOfPage > 1
})

const pages: ComputedRef<Page[]> = computed(() => {
  const result: Page[] = [1]

  const middleStart = Math.max(
    Math.min(
      props.currentPage - 1,
      props.numberOfPage - thresholdToShowTheEllipsis - 2
    ),
    3
  )
  const middleEnd = Math.min(middleStart + 2, props.numberOfPage - 2)

  // first ellipsis
  if (middleStart - 1 > thresholdToShowTheEllipsis) {
    result.push(ellipsis)
  } else if (props.numberOfPage >= 3) {
    result.push(2)
  }

  // middle pages
  for (let i = middleStart; i <= middleEnd; i++) {
    result.push(i)
  }

  // last ellipsis
  if (props.numberOfPage - middleEnd > thresholdToShowTheEllipsis) {
    result.push(ellipsis)
  } else if (props.numberOfPage >= 4) {
    result.push(props.numberOfPage - 1)
  }

  result.push(props.numberOfPage)
  return result
})

function itemClasses(page: Page) {
  return {
    'pagination__item--active': isItemActive(page),
    pagination__ellipsis: page == ellipsis,
  }
}

function isItemActive(page: Page) {
  if (typeof page == 'string') return
  return page == props.currentPage
}

function itemClick(page: Page) {
  if (typeof page == 'string') return
  emit('input', page)
}
</script>
